import { Fragment, useState } from 'react';
import { i18n } from '@lingui/core';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AvatarWithLabel, Badge, Box, Chip, IconButton, Menu, Tooltip } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { EsigningAttachmentKind } from 'shared/domains/apollo/generated/types';
import { ConfirmRemoveDialog } from './ConfirmRemoveDialog';
import { EditDialog } from './EditDialog';
import { getAttachmentSubtitle } from '../utils';
import { Container } from './styled';
export function DocumentAttachmentItem(props) {
    const { attachment, disabled = false, embeddedCount = 0, onDownload, onRename, onRemove, onScrollToAttachment, onEmbed, } = props;
    const { name, url, kind, createdAt, updatedAt } = attachment;
    const [renderEditDialog, setRenderEditDialog] = useState(false);
    const [renderRemoveDialog, setRenderRemoveDialog] = useState(false);
    const subtitle = getAttachmentSubtitle(createdAt, updatedAt, kind);
    const showMenuActions = kind !== EsigningAttachmentKind.SignRequest;
    const isEmbedded = embeddedCount > 0;
    const hideEditDialog = () => setRenderEditDialog(false);
    const hideRemoveDialog = () => setRenderRemoveDialog(false);
    const showEditDialog = () => {
        setRenderEditDialog(true);
    };
    const showRemoveDialog = () => {
        setRenderRemoveDialog(true);
    };
    const handleRemoveAttachment = () => {
        if (onRemove) {
            onRemove();
        }
    };
    const handleRenameAttachment = (newName) => {
        if (onRename) {
            onRename(newName);
        }
    };
    const handleDownloadAttachment = () => {
        if (onDownload) {
            onDownload();
        }
    };
    const handleEmbed = () => {
        if (onEmbed) {
            onEmbed(attachment);
        }
    };
    const handlePreview = (evt) => {
        evt.preventDefault();
        if (url) {
            window.open(url, '_blank');
        }
    };
    return (<Fragment>
      {renderRemoveDialog && (<ConfirmRemoveDialog embedded={isEmbedded} onCancel={hideRemoveDialog} onConfirm={handleRemoveAttachment}/>)}
      {renderEditDialog && <EditDialog initialName={name} onClose={hideEditDialog} onEdit={handleRenameAttachment}/>}
      <Container>
        <AvatarWithLabel solid size="sm" title={name} subtitle={i18n._(subtitle)} appearance="info">
          <Falcon icon="paperclip"/>
        </AvatarWithLabel>
        <Box alignItems="center" gap="md">
          {isEmbedded && (<Tooltip side="left" title={t({
                comment: 'Embedded attachment tooltip',
                message: 'This attachment is embedded in the document',
            })}>
              <Chip size="small" appearance="info" label={t({ message: 'Embedded', comment: 'Embedded attachment label' })}/>
            </Tooltip>)}

          <Menu.Root target={<IconButton $round size="xs" appearance="ghost">
                <Falcon icon="ellipsis-v"/>
              </IconButton>}>
            {isEmbedded && onScrollToAttachment && showMenuActions && (<Menu.Item disabled={disabled} onClick={onScrollToAttachment}>
                <Falcon icon="magnifying-glass"/>
                <Trans comment="Menu label for scrolling to attachment">Show in document</Trans>
                <Box ml="auto">
                  <Badge size="small" appearance="secondary" value={embeddedCount}/>
                </Box>
              </Menu.Item>)}
            {onEmbed && showMenuActions && (<Menu.Item disabled={disabled} onClick={handleEmbed}>
                <Falcon icon="paperclip"/>
                <Trans comment="Menu label for embedding attachment">Embed</Trans>
              </Menu.Item>)}
            {url && (<Menu.Item asChild onClick={handlePreview}>
                <a href={url} target="_blank" rel="noreferrer">
                  <Falcon icon="eye"/>
                  <Trans comment="Menu label for opening attachment link">Preview</Trans>
                </a>
              </Menu.Item>)}
            {onDownload && (<Menu.Item onClick={handleDownloadAttachment}>
                <Falcon icon="down-to-line"/>
                <Trans comment="Menu label for downloading attachment">Download</Trans>
              </Menu.Item>)}
            {onRename && showMenuActions && (<Menu.Item disabled={disabled} onClick={showEditDialog}>
                <Falcon icon="pencil"/>
                <Trans comment="Menu label for editing attachment name">Edit name</Trans>
              </Menu.Item>)}
            {onRemove && showMenuActions && (<Menu.Item disabled={disabled} onClick={showRemoveDialog}>
                <Falcon icon="trash"/>
                <Trans comment="Menu label for removing attachment">Remove</Trans>
              </Menu.Item>)}
          </Menu.Root>
        </Box>
      </Container>
    </Fragment>);
}
